/**
 * BYK-22: Real-Time Conflict Highlighting & Smart Errors
 *
 * Service for detecting spatial conflicts, safety violations, and design issues
 * in the kitchen floor plan.
 */

import type {
  Conflict,
  ConflictType,
  ConflictSeverity,
  ConflictDetectionResult,
  PlacedVolume,
  WallSegment,
  StructuralElement,
  ProductResponse,
  RoomConfig,
  Point2D,
} from "@byk/shared";
import {
  getCellsCoveredByVolume,
  getCellsCoveredByWall,
  cellKey,
} from "./occupancy";

// =============================================================================
// Configuration Constants
// =============================================================================

/** Minimum clearance in cm required between meat and dairy items (Kashrut) */
const KASHRUT_MIN_CLEARANCE = 60;

/** Window safety zone for heat sources (cm from window centerline) */
const WINDOW_HEAT_CLEARANCE = 40;

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Calculate Euclidean distance between two points
 */
function distance2D(p1: Point2D, p2: Point2D): number {
  const dx = p1.x - p2.x;
  const dy = p1.y - p2.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Check if two rectangles overlap
 */
function rectanglesOverlap(
  r1: { x: number; y: number; width: number; depth: number },
  r2: { x: number; y: number; width: number; depth: number }
): boolean {
  return (
    r1.x < r2.x + r2.width &&
    r1.x + r1.width > r2.x &&
    r1.y < r2.y + r2.depth &&
    r1.y + r1.depth > r2.y
  );
}

/**
 * Get the bounding box of a volume considering rotation
 */
function getVolumeBounds(
  volume: PlacedVolume,
  dimensions: { width: number; depth: number }
): { x: number; y: number; width: number; depth: number } {
  const rotation = volume.rotation || 0;
  const is90 = rotation === 90 || rotation === 270;

  return {
    x: volume.position.x,
    y: volume.position.y,
    width: is90 ? dimensions.depth : dimensions.width,
    depth: is90 ? dimensions.width : dimensions.depth,
  };
}

/**
 * Get the center point of a volume
 */
function getVolumeCenter(
  volume: PlacedVolume,
  dimensions: { width: number; depth: number }
): Point2D {
  const bounds = getVolumeBounds(volume, dimensions);
  return {
    x: bounds.x + bounds.width / 2,
    y: bounds.y + bounds.depth / 2,
  };
}

/**
 * Generate a unique conflict ID
 */
function generateConflictId(type: ConflictType, volumeIds: string[]): string {
  return `${type}-${volumeIds.sort().join("-")}-${Date.now()}`;
}

// =============================================================================
// Conflict Detection Functions
// =============================================================================

export interface ConflictDetectionParams {
  room: RoomConfig;
  walls: WallSegment[];
  elements: StructuralElement[];
  volumes: PlacedVolume[];
  products: ProductResponse[];
  cellSize?: number;
}

/**
 * Detect overlapping volumes
 */
function detectOverlaps(
  volumes: PlacedVolume[],
  products: ProductResponse[]
): Conflict[] {
  const conflicts: Conflict[] = [];
  const productMap = new Map(products.map((p) => [p.id, p]));

  for (let i = 0; i < volumes.length; i++) {
    const v1 = volumes[i];
    const p1 = productMap.get(v1.productId);
    if (!p1?.dimensions) continue;

    const bounds1 = getVolumeBounds(v1, p1.dimensions);

    for (let j = i + 1; j < volumes.length; j++) {
      const v2 = volumes[j];
      const p2 = productMap.get(v2.productId);
      if (!p2?.dimensions) continue;

      const bounds2 = getVolumeBounds(v2, p2.dimensions);

      if (rectanglesOverlap(bounds1, bounds2)) {
        conflicts.push({
          id: generateConflictId("overlap", [v1.id, v2.id]),
          type: "overlap",
          severity: "error",
          message: `${p1.name} overlaps with ${p2.name}`,
          suggestion: `Move one of the items to eliminate the overlap`,
          volumeIds: [v1.id, v2.id],
          area: {
            x: Math.max(bounds1.x, bounds2.x),
            y: Math.max(bounds1.y, bounds2.y),
            width:
              Math.min(bounds1.x + bounds1.width, bounds2.x + bounds2.width) -
              Math.max(bounds1.x, bounds2.x),
            height:
              Math.min(bounds1.y + bounds1.depth, bounds2.y + bounds2.depth) -
              Math.max(bounds1.y, bounds2.y),
          },
        });
      }
    }
  }

  return conflicts;
}

/**
 * Detect wall collisions
 */
function detectWallCollisions(
  volumes: PlacedVolume[],
  walls: WallSegment[],
  products: ProductResponse[],
  cellSize: number
): Conflict[] {
  const conflicts: Conflict[] = [];
  const productMap = new Map(products.map((p) => [p.id, p]));

  // Build set of wall cell keys
  const wallCells = new Set<string>();
  for (const wall of walls) {
    const cells = getCellsCoveredByWall(wall, cellSize);
    for (const cell of cells) {
      wallCells.add(cellKey(cell.x, cell.y));
    }
  }

  // Check each volume against wall cells
  for (const volume of volumes) {
    const product = productMap.get(volume.productId);
    if (!product?.dimensions) continue;

    const { bodyCells } = getCellsCoveredByVolume(
      volume,
      product.dimensions,
      cellSize,
      false
    );

    const collidingCells: Point2D[] = [];
    for (const cell of bodyCells) {
      if (wallCells.has(cellKey(cell.x, cell.y))) {
        collidingCells.push(cell);
      }
    }

    if (collidingCells.length > 0) {
      const avgX =
        collidingCells.reduce((sum, c) => sum + c.x, 0) / collidingCells.length;
      const avgY =
        collidingCells.reduce((sum, c) => sum + c.y, 0) / collidingCells.length;

      conflicts.push({
        id: generateConflictId("wall-collision", [volume.id]),
        type: "wall-collision",
        severity: "error",
        message: `${product.name} collides with wall`,
        suggestion: `Move the item away from the wall`,
        volumeIds: [volume.id],
        position: { x: avgX, y: avgY },
      });
    }
  }

  return conflicts;
}

/**
 * Detect stove/cooktop placed under window (fire hazard)
 */
function detectStoveUnderWindow(
  volumes: PlacedVolume[],
  elements: StructuralElement[],
  walls: WallSegment[],
  products: ProductResponse[]
): Conflict[] {
  const conflicts: Conflict[] = [];
  const productMap = new Map(products.map((p) => [p.id, p]));

  // Find windows
  const windows = elements.filter((e) => e.type === "window");
  if (windows.length === 0) return conflicts;

  // Find heat sources (cooktops, ovens)
  const heatSources = volumes.filter((v) => {
    const semantics = v.semantics;
    return semantics?.isHeatSource;
  });

  for (const heatSource of heatSources) {
    const product = productMap.get(heatSource.productId);
    if (!product?.dimensions) continue;

    const center = getVolumeCenter(heatSource, product.dimensions);

    for (const window of windows) {
      // Get window position on wall
      const wall = walls.find((w) => w.id === window.wallId);
      if (!wall) continue;

      // Calculate window center position (simplified)
      const dx = wall.end.x - wall.start.x;
      const dy = wall.end.y - wall.start.y;
      const length = Math.sqrt(dx * dx + dy * dy);
      const t = window.offset / length;
      const windowCenter: Point2D = {
        x: wall.start.x + dx * t + (dx / length) * (window.width / 2),
        y: wall.start.y + dy * t + (dy / length) * (window.width / 2),
      };

      // Check if heat source is near window
      const dist = distance2D(center, windowCenter);
      if (dist < WINDOW_HEAT_CLEARANCE + window.width / 2) {
        conflicts.push({
          id: generateConflictId("stove-under-window", [heatSource.id]),
          type: "stove-under-window",
          severity: "warning",
          message: `${product.name} is placed under/near a window`,
          suggestion: `Move the ${product.name} away from windows - curtains near heat sources are a fire hazard`,
          volumeIds: [heatSource.id],
          position: center,
        });
      }
    }
  }

  return conflicts;
}

/**
 * Detect blocked access zones
 */
function detectBlockedAccess(
  volumes: PlacedVolume[],
  products: ProductResponse[],
  cellSize: number
): Conflict[] {
  const conflicts: Conflict[] = [];
  const productMap = new Map(products.map((p) => [p.id, p]));

  // Get volumes that require front access
  const accessRequired = volumes.filter((v) => v.semantics?.requiresFrontAccess);

  for (const reqVolume of accessRequired) {
    const reqProduct = productMap.get(reqVolume.productId);
    if (!reqProduct?.dimensions) continue;

    const { accessCells } = getCellsCoveredByVolume(
      reqVolume,
      reqProduct.dimensions,
      cellSize,
      true
    );

    // Check if any other volume occupies the access zone
    for (const otherVolume of volumes) {
      if (otherVolume.id === reqVolume.id) continue;

      const otherProduct = productMap.get(otherVolume.productId);
      if (!otherProduct?.dimensions) continue;

      const { bodyCells } = getCellsCoveredByVolume(
        otherVolume,
        otherProduct.dimensions,
        cellSize,
        false
      );

      const otherCellKeys = new Set(bodyCells.map((c) => cellKey(c.x, c.y)));
      const blocking = accessCells.some((c) =>
        otherCellKeys.has(cellKey(c.x, c.y))
      );

      if (blocking) {
        conflicts.push({
          id: generateConflictId("blocked-access", [reqVolume.id, otherVolume.id]),
          type: "blocked-access",
          severity: "warning",
          message: `${otherProduct.name} blocks access to ${reqProduct.name}`,
          suggestion: `Move ${otherProduct.name} to allow door/drawer opening`,
          volumeIds: [reqVolume.id, otherVolume.id],
        });
      }
    }
  }

  return conflicts;
}

/**
 * Detect Kashrut proximity violations (meat/dairy too close)
 */
function detectKashrutProximity(
  volumes: PlacedVolume[],
  products: ProductResponse[]
): Conflict[] {
  const conflicts: Conflict[] = [];
  const productMap = new Map(products.map((p) => [p.id, p]));

  // Separate meat and dairy volumes
  const meatVolumes = volumes.filter(
    (v) => v.isKashrutDistinct && v.zone === "cooking"
  );
  const dairyVolumes = volumes.filter(
    (v) => v.isKashrutDistinct && v.zone === "prep"
  );

  // If no kashrut distinctions, skip
  if (meatVolumes.length === 0 || dairyVolumes.length === 0) return conflicts;

  for (const meatVolume of meatVolumes) {
    const meatProduct = productMap.get(meatVolume.productId);
    if (!meatProduct?.dimensions) continue;
    const meatCenter = getVolumeCenter(meatVolume, meatProduct.dimensions);

    for (const dairyVolume of dairyVolumes) {
      const dairyProduct = productMap.get(dairyVolume.productId);
      if (!dairyProduct?.dimensions) continue;
      const dairyCenter = getVolumeCenter(dairyVolume, dairyProduct.dimensions);

      const dist = distance2D(meatCenter, dairyCenter);

      if (dist < KASHRUT_MIN_CLEARANCE) {
        conflicts.push({
          id: generateConflictId("kashrut-proximity", [
            meatVolume.id,
            dairyVolume.id,
          ]),
          type: "kashrut-proximity",
          severity: "warning",
          message: `Meat and dairy items are too close (${Math.round(dist)}cm < ${KASHRUT_MIN_CLEARANCE}cm)`,
          suggestion: `Separate meat and dairy zones by at least ${KASHRUT_MIN_CLEARANCE}cm`,
          volumeIds: [meatVolume.id, dairyVolume.id],
        });
      }
    }
  }

  return conflicts;
}

/**
 * Detect ventilation issues (heat sources without clearance above)
 */
function detectVentilationBlocked(
  volumes: PlacedVolume[],
  products: ProductResponse[]
): Conflict[] {
  const conflicts: Conflict[] = [];
  const productMap = new Map(products.map((p) => [p.id, p]));

  // Find volumes that require ventilation
  const ventRequired = volumes.filter((v) => v.semantics?.requiresVentilation);

  for (const reqVolume of ventRequired) {
    const product = productMap.get(reqVolume.productId);
    if (!product?.dimensions) continue;

    // Check if there's sufficient clearance above (simplified - just a warning)
    // In a real implementation, we'd check for overhead cabinets
    const position = reqVolume.position;

    // For now, just generate an info-level reminder
    conflicts.push({
      id: generateConflictId("ventilation-blocked", [reqVolume.id]),
      type: "ventilation-blocked",
      severity: "info",
      message: `${product.name} requires proper ventilation`,
      suggestion: `Ensure a range hood or exhaust fan is installed above the cooktop`,
      volumeIds: [reqVolume.id],
      position: { x: position.x, y: position.y },
    });
  }

  return conflicts;
}

// =============================================================================
// Main Detection Function
// =============================================================================

/**
 * Run all conflict detection checks and return combined results
 */
export function detectConflicts(
  params: ConflictDetectionParams
): ConflictDetectionResult {
  const { walls, elements, volumes, products, cellSize = 10 } = params;

  const allConflicts: Conflict[] = [];

  // Run all detection functions
  allConflicts.push(...detectOverlaps(volumes, products));
  allConflicts.push(...detectWallCollisions(volumes, walls, products, cellSize));
  allConflicts.push(...detectStoveUnderWindow(volumes, elements, walls, products));
  allConflicts.push(...detectBlockedAccess(volumes, products, cellSize));
  allConflicts.push(...detectKashrutProximity(volumes, products));
  allConflicts.push(...detectVentilationBlocked(volumes, products));

  // Count by severity
  const errorCount = allConflicts.filter((c) => c.severity === "error").length;
  const warningCount = allConflicts.filter((c) => c.severity === "warning").length;
  const infoCount = allConflicts.filter((c) => c.severity === "info").length;

  return {
    conflicts: allConflicts,
    errorCount,
    warningCount,
    infoCount,
    isValid: errorCount === 0,
  };
}

/**
 * Get conflicts grouped by type
 */
export function groupConflictsByType(
  conflicts: Conflict[]
): Map<ConflictType, Conflict[]> {
  const grouped = new Map<ConflictType, Conflict[]>();

  for (const conflict of conflicts) {
    if (!grouped.has(conflict.type)) {
      grouped.set(conflict.type, []);
    }
    grouped.get(conflict.type)!.push(conflict);
  }

  return grouped;
}

/**
 * Get conflicts for a specific volume
 */
export function getConflictsForVolume(
  conflicts: Conflict[],
  volumeId: string
): Conflict[] {
  return conflicts.filter((c) => c.volumeIds.includes(volumeId));
}

/**
 * Get the highest severity conflict for a volume
 */
export function getHighestSeverity(
  conflicts: Conflict[]
): ConflictSeverity | null {
  if (conflicts.length === 0) return null;
  if (conflicts.some((c) => c.severity === "error")) return "error";
  if (conflicts.some((c) => c.severity === "warning")) return "warning";
  return "info";
}
